/**
 * useDate Composable
 *
 * Centralized date utilities using dayjs.
 * Replaces vue-moment plugin with a modern composable approach.
 *
 * Features:
 * - Locale support (Italian by default)
 * - Common date formatting patterns
 * - Relative time (timeago)
 * - Date manipulation
 * - Business day calculations
 *
 * @version 2.0.0-alpha.10
 * @see https://day.js.org/docs/en/installation/installation
 */

import dayjs, { type Dayjs, type ConfigType } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/it'

// Extend dayjs with plugins
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(weekOfYear)
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(timezone)

// Set default locale to Italian
dayjs.locale('it')

// Set default timezone
dayjs.tz.setDefault('Europe/Rome')

// =============================================================================
// Types
// =============================================================================

export type DateInput = ConfigType

export interface UseDateReturn {
  /** dayjs instance */
  dayjs: typeof dayjs
  /** Format date with common patterns */
  format: (date: DateInput, pattern?: string) => string
  /** Format date for display (optional custom format, default DD/MM/YYYY) */
  formatDate: (date: DateInput, formatStr?: string) => string
  /** Format date and time for display (optional custom format, default DD/MM/YYYY HH:mm) */
  formatDateTime: (date: DateInput, formatStr?: string) => string
  /** Format time for display */
  formatTime: (date: DateInput) => string
  /** Format date for API (ISO 8601) */
  formatApi: (date: DateInput) => string
  /** Get relative time (e.g., "2 ore fa") */
  fromNow: (date: DateInput) => string
  /** Get relative time to a specific date */
  from: (date: DateInput, compareDate: DateInput) => string
  /** Get relative time in the future (e.g., "tra 2 ore") */
  toNow: (date: DateInput) => string
  /** Check if date is today */
  isToday: (date: DateInput) => boolean
  /** Check if date is yesterday */
  isYesterday: (date: DateInput) => boolean
  /** Check if date is tomorrow */
  isTomorrow: (date: DateInput) => boolean
  /** Check if date is in the past */
  isPast: (date: DateInput) => boolean
  /** Check if date is in the future */
  isFuture: (date: DateInput) => boolean
  /** Check if date is this week */
  isThisWeek: (date: DateInput) => boolean
  /** Check if date is this month */
  isThisMonth: (date: DateInput) => boolean
  /** Parse date string */
  parse: (date: DateInput, format?: string) => Dayjs
  /** Get start of day */
  startOfDay: (date: DateInput) => Dayjs
  /** Get end of day */
  endOfDay: (date: DateInput) => Dayjs
  /** Get duration between two dates */
  getDuration: (start: DateInput, end: DateInput) => string
  /** Add business days to date */
  addBusinessDays: (date: DateInput, days: number) => Dayjs
  /** Get week number */
  getWeekNumber: (date: DateInput) => number
  /** Format calendar date (oggi, ieri, domani, or date) */
  calendar: (date: DateInput) => string
}

// =============================================================================
// Date Format Patterns
// =============================================================================

export const DATE_FORMATS = {
  // Italian formats
  DATE: 'DD/MM/YYYY',
  DATE_SHORT: 'DD/MM/YY',
  DATE_LONG: 'D MMMM YYYY',
  DATE_FULL: 'dddd D MMMM YYYY',
  
  // Time formats
  TIME: 'HH:mm',
  TIME_SECONDS: 'HH:mm:ss',
  
  // Combined formats
  DATETIME: 'DD/MM/YYYY HH:mm',
  DATETIME_SECONDS: 'DD/MM/YYYY HH:mm:ss',
  DATETIME_LONG: 'D MMMM YYYY, HH:mm',
  
  // API formats
  API_DATE: 'YYYY-MM-DD',
  API_DATETIME: 'YYYY-MM-DDTHH:mm:ss',
  API_ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  
  // Month/Year formats
  MONTH_YEAR: 'MMMM YYYY',
  MONTH_SHORT: 'MMM YYYY',
  YEAR: 'YYYY'
} as const

// =============================================================================
// Composable
// =============================================================================

/**
 * Composable for date operations
 *
 * @example
 * ```ts
 * const { formatDate, formatDateTime, fromNow, isToday } = useDate()
 *
 * // Format dates
 * const date = formatDate('2024-01-15') // "15/01/2024"
 * const datetime = formatDateTime('2024-01-15T10:30:00') // "15/01/2024 10:30"
 *
 * // Relative time
 * const ago = fromNow('2024-01-10') // "5 giorni fa"
 *
 * // Check date
 * if (isToday(someDate)) {
 *   // ...
 * }
 *
 * // Custom format
 * const custom = format(date, 'dddd D MMMM') // "lunedì 15 gennaio"
 * ```
 */
export function useDate(): UseDateReturn {
  // Format with custom pattern
  function format(date: DateInput, pattern: string = DATE_FORMATS.DATE): string {
    if (!date) return ''
    return dayjs(date).format(pattern)
  }

  // Common format helpers
  function formatDate(date: DateInput, formatStr?: string): string {
    return format(date, formatStr ?? DATE_FORMATS.DATE)
  }

  function formatDateTime(date: DateInput, formatStr?: string): string {
    return format(date, formatStr ?? DATE_FORMATS.DATETIME)
  }

  function formatTime(date: DateInput): string {
    return format(date, DATE_FORMATS.TIME)
  }

  function formatApi(date: DateInput): string {
    return format(date, DATE_FORMATS.API_DATE)
  }

  // Relative time
  function fromNow(date: DateInput): string {
    if (!date) return ''
    return dayjs(date).fromNow()
  }

  function from(date: DateInput, compareDate: DateInput): string {
    if (!date || !compareDate) return ''
    return dayjs(date).from(dayjs(compareDate))
  }

  function toNow(date: DateInput): string {
    if (!date) return ''
    return dayjs(date).toNow()
  }

  // Date checks
  function isToday(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs(), 'day')
  }

  function isYesterday(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day')
  }

  function isTomorrow(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs().add(1, 'day'), 'day')
  }

  function isPast(date: DateInput): boolean {
    return dayjs(date).isBefore(dayjs())
  }

  function isFuture(date: DateInput): boolean {
    return dayjs(date).isAfter(dayjs())
  }

  function isThisWeek(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs(), 'week')
  }

  function isThisMonth(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs(), 'month')
  }

  // Parsing
  function parse(date: DateInput, formatStr?: string): Dayjs {
    if (formatStr) {
      return dayjs(date, formatStr)
    }
    return dayjs(date)
  }

  // Day boundaries
  function startOfDay(date: DateInput): Dayjs {
    return dayjs(date).startOf('day')
  }

  function endOfDay(date: DateInput): Dayjs {
    return dayjs(date).endOf('day')
  }

  // Duration
  function getDuration(start: DateInput, end: DateInput): string {
    const diff = dayjs(end).diff(dayjs(start))
    const dur = dayjs.duration(diff)
    
    const days = Math.floor(dur.asDays())
    const hours = dur.hours()
    const minutes = dur.minutes()
    
    const parts: string[] = []
    if (days > 0) parts.push(`${days}g`)
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)
    
    return parts.join(' ')
  }

  // Business days
  function addBusinessDays(date: DateInput, days: number): Dayjs {
    let result = dayjs(date)
    let addedDays = 0
    
    while (addedDays < days) {
      result = result.add(1, 'day')
      // Skip weekends (0 = Sunday, 6 = Saturday)
      const dayOfWeek = result.day()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        addedDays++
      }
    }
    
    return result
  }

  // Week number
  function getWeekNumber(date: DateInput): number {
    return dayjs(date).week()
  }

  // Calendar formatting
  function calendar(date: DateInput): string {
    if (!date) return ''
    
    const d = dayjs(date)
    const now = dayjs()
    
    if (d.isSame(now, 'day')) {
      return `Oggi, ${d.format(DATE_FORMATS.TIME)}`
    }
    if (d.isSame(now.subtract(1, 'day'), 'day')) {
      return `Ieri, ${d.format(DATE_FORMATS.TIME)}`
    }
    if (d.isSame(now.add(1, 'day'), 'day')) {
      return `Domani, ${d.format(DATE_FORMATS.TIME)}`
    }
    if (d.isSame(now, 'week')) {
      return d.format('dddd, HH:mm')
    }
    
    return d.format(DATE_FORMATS.DATETIME)
  }

  return {
    dayjs,
    format,
    formatDate,
    formatDateTime,
    formatTime,
    formatApi,
    fromNow,
    from,
    toNow,
    isToday,
    isYesterday,
    isTomorrow,
    isPast,
    isFuture,
    isThisWeek,
    isThisMonth,
    parse,
    startOfDay,
    endOfDay,
    getDuration,
    addBusinessDays,
    getWeekNumber,
    calendar
  }
}

// Export dayjs instance for direct use
export { dayjs }

export default useDate
